<div class="page-wrapper">

    <!-- Toast Notification -->
    <div class="toast-container" *ngIf="showToast" [ngClass]="'toast-' + toastType">
        <div class="toast-content">
            <svg *ngIf="toastType === 'success'" class="toast-icon" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <svg *ngIf="toastType === 'error'" class="toast-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <span class="toast-message">{{ toastMessage }}</span>
        </div>
        <button class="toast-close" (click)="showToast = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        </button>
    </div>

    <!-- Confirmation Modal -->
    <div class="modal-overlay" *ngIf="showConfirmModal">
        <div class="modal-content">
            <h3 class="modal-title">{{ 'dash.confirmSchedule' | translate }}</h3>
            <p class="modal-text">{{ 'dash.confirmScheduleText' | translate }}</p>
            <div class="modal-actions">
                <button class="btn btn-outline-blue" (click)="cancelScheduleInterview()">
                    {{ 'Cancel' | translate }}
                </button>
                <button class="btn btn-gradient" (click)="confirmScheduleInterview()">
                    {{ 'dash.confirm' | translate }}
                </button>
            </div>
        </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="flex items-center justify-center py-24">
        <div class="flex flex-col items-center gap-3">
            <svg class="animate-spin w-8 h-8 text-rose-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <p class="text-sm text-gray-400">{{ 'appDetails.loading' | translate }}</p>
        </div>
    </div>

    <!-- Error State -->
    <div *ngIf="!isLoading && errorMessage" class="flex flex-col items-center justify-center py-20 text-center gap-3">
        <p class="text-sm text-gray-500">{{ errorMessage }}</p>
        <button (click)="getApplicationDetails(applicationId!)"
            class="mt-1 px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition">
            {{ 'appDetails.retry' | translate }}
        </button>
    </div>

    <!-- Main Content -->
    <ng-container *ngIf="!isLoading && !errorMessage && applicationDetails">

        <!-- ───────── Candidate Header ───────── -->
        <div class="card header-card">
            <div class="header-left">
                <div class="avatar-wrapper">
                    <div class="avatar avatar-initials">
                        {{ applicationDetails.applicantName.charAt(0) }}
                    </div>
                </div>
                <div class="header-info">
                    <div class="name-row">
                        <h2 class="candidate-name">{{ applicationDetails.applicantName }}</h2>
                        <span class="tag" [ngClass]="getStatusBadgeClass(applicationDetails.status)">
                            {{ applicationDetails.status }}
                        </span>
                    </div>
                    <p class="applied-meta">
                        {{ 'appDetails.appliedFor' | translate }} <strong>{{ applicationDetails.jobPostTitle }}</strong>
                        &nbsp;•&nbsp; {{ applicationDetails.companyName }}
                        &nbsp;•&nbsp; {{ 'appDetails.applied' | translate }} {{ getTimeAgo(applicationDetails.appliedAt)
                        }} {{ 'appDetails.ago' | translate }}
                    </p>
                    <p class="applied-meta text-xs text-gray-400 mt-0.5">
                        {{ applicationDetails.applicantEmail }}
                    </p>
                </div>
            </div>
            <div class="header-actions">
                <button class="btn btn-gradient" (click)="onViewProfile()">
                    {{ 'appDetails.viewCandidateProfile' | translate }}
                </button>
                <button class="btn btn-icon" (click)="onDownloadCV()" aria-label="Download CV"
                    [disabled]="!applicationDetails.cvUrl">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                </button>
            </div>
        </div>

        <!-- ───────── Uploaded CV ───────── -->

        <div class="card" *ngIf="applicationDetails.cvUrl">
            <div class="section-header">
                <span class="section-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F98650" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                    </svg>
                </span>
                <h3 class="section-title">{{ 'appDetails.uploadedCV' | translate }}</h3>
                <!-- Download badge uses plain URL with token NOT needed for download -->
                <a [href]="getCvUrl(applicationDetails.cvUrl)" target="_blank" class="cv-badge" download>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                    </svg>
                    {{ getCvFileName() }}
                </a>
            </div>

            <!-- CV loading spinner -->
            <div *ngIf="isCvLoading" class="flex items-center justify-center py-10">
                <svg class="animate-spin w-6 h-6 text-rose-400" xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
            </div>

            <!-- CV error -->
            <div *ngIf="!isCvLoading && cvError" class="text-sm text-red-400 px-2 py-4 text-center">
                {{ 'appDetails.couldNotLoadCV' | translate }}
                <a [href]="getCvUrl(applicationDetails.cvUrl)" target="_blank" class="underline text-orange-500">{{
                    'appDetails.openDirectly' | translate }}</a>
            </div>

            <!-- PDF preview via blob URL (carries auth token) -->
            <div class="cv-preview-container" *ngIf="!isCvLoading && !cvError && cvBlobUrl">
                <iframe [src]="cvBlobUrl" class="cv-preview-iframe" title="CV Preview">
                </iframe>
            </div>
        </div>

        <!-- No CV uploaded -->
        <div class="card" *ngIf="!applicationDetails.cvUrl">
            <div class="section-header">
                <span class="section-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F98650" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                    </svg>
                </span>
                <h3 class="section-title">{{ 'appDetails.uploadedCV' | translate }}</h3>
            </div>
            <p class="text-sm text-gray-400 px-2 py-4">{{ 'appDetails.noCVUploaded' | translate }}</p>
        </div>

        <!-- ───────── Application Questions ───────── -->
        <div class="card">
            <div class="section-header">
                <span class="section-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F98650" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                </span>
                <h3 class="section-title">{{ 'appDetails.applicationQuestions' | translate }}</h3>
            </div>

            <div class="questions-list" *ngIf="applicationDetails.answers && applicationDetails.answers.length > 0">
                <div class="question-block" *ngFor="let qa of applicationDetails.answers ">
                    <p class="question-label">{{ qa.question }}</p>
                    <div class="answer-box">{{ qa.answer }}</div>
                </div>
            </div>

            <div *ngIf="!applicationDetails.answers || applicationDetails.answers.length === 0"
                class="text-sm text-gray-400 px-2 py-4">
                {{ 'appDetails.noAnswersSubmitted' | translate }}
            </div>

            <!-- Application Summary -->

        </div>

        <!-- ───────── Application Status ───────── -->
        <div class="card">
            <h3 class="section-title no-icon">{{ 'appDetails.applicationStatus' | translate }}</h3>

            <div class="status-actions">
                <button class="btn btn-gradient status-btn" (click)="onShortlist()"
                    [disabled]="isUpdatingStatus || applicationDetails.status === 'SHORTLISTED'">
                    <svg *ngIf="updatingStatus === 'SHORTLISTED'" class="status-spinner animate-spin" xmlns="http://www.w3.org/2000/svg"
                        fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                        </circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    {{ 'appDetails.shortlistCandidate' | translate }}
                </button>

                <button class="btn btn-outline-blue status-btn interview-coming-soon" disabled
                    [title]="'Coming Soon' | translate"
                    [attr.aria-label]="('appDetails.scheduleInterview' | translate) + ' - ' + ('Coming Soon' | translate)">
                    {{ 'appDetails.scheduleInterview' | translate }}
                    <span class="coming-soon-label">({{ 'Coming Soon' | translate }})</span>
                </button>

                <button class="btn btn-outline-red status-btn" (click)="onRejectApplication()"
                    [disabled]="isUpdatingStatus || applicationDetails.status === 'REJECTED'">
                    <svg *ngIf="updatingStatus === 'REJECTED'" class="status-spinner animate-spin" xmlns="http://www.w3.org/2000/svg"
                        fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    {{ 'appDetails.rejectApplication' | translate }}
                </button>
            </div>
        </div>

        <div class="card">

            <h4 class="summary-title">{{ 'appDetails.applicationSummary' | translate }}</h4>
            <div class="summary-meta">
                <span class="meta-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {{ 'appDetails.appliedOn' | translate }} {{ formatDate(applicationDetails.appliedAt) }}
                </span>
                <span class="meta-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                    </svg>
                    {{ applicationDetails.companyName }}
                </span>
                <span class="meta-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12
                       19.79 19.79 0 0 1 1.61 3.45 2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72
                       12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 8a16 16 0 0 0 6 6
                       l.27-.81a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    {{ applicationDetails.applicantEmail }}
                </span>
            </div>
        </div>


        <!-- ───────── Internal HR Notes ───────── -->
        <div class="card">
            <div class="section-header">
                <span class="section-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F98650" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                    </svg>
                </span>
                <h3 class="section-title">{{ 'appDetails.internalHRNotes' | translate }}</h3>
            </div>




            <textarea class="hr-textarea" [placeholder]="'appDetails.notesPlaceholder' | translate" [(ngModel)]="hrNote"
                rows="4"></textarea>
            <div class="note-footer">
                <button class="btn btn-gradient save-btn" (click)="onSaveNote()" [disabled]="isUpdatingStatus">
                    <svg *ngIf="isSavingNote" class="status-spinner animate-spin" xmlns="http://www.w3.org/2000/svg"
                        fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                        </circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    {{ 'appDetails.saveNotes' | translate }}
                </button>
            </div>
        </div>

    </ng-container>
</div>
